import type {
  CalibrationRecord,
  ExportJob,
  ExportRequest,
  Recipe,
  RecipePayload,
  Sample,
  SimulationResult,
  ValidationResult,
} from '@ctcells/contract'
import { platform, REQUEST_TIMEOUT_MS } from '@ctcells/core'
import { t } from '@ctcells/i18n'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { ApiError } from '../errors.ts'
import { keys } from '../keys.ts'
import { exportDownloadPath } from '../queries/resources.ts'

/**
 * 리소스 쓰기 — 시료 · 레시피 · 시약 · 폐기통 · 보정 · 내보내기.
 * ⚠ 랩웨어 카탈로그 쓰기(`/labware-definitions`)는 2026-10-11 에 걷었다 — 서버 v0.9.4 가 창구를 걷었다(core `1cd3f92a`).
 *   랩웨어 정의는 이제 템플릿(`commands/templates.ts` · `kind=labware`)으로 쓴다.
 *
 * ⚠ **「전부 실물에 쏴 보지 않았다」는 옛말이다. 되살리지 마라.** 로컬 컨테이너에 쓰는 것은
 *   허용돼 있고(eric 확정 2026-09-20), 아래는 실물 응답까지 봤다 —
 *   `POST /recipes`(201 · `v1 committed`) · `PUT /samples/{id}`(200 · 보낸 칸만 반영) ·
 *   (그때 함께 쏴 본 `/labware-definitions` 셋은 창구째 걷혔다).
 *   ⊕ `POST /calibrations`(201 · 2026-09-21) — 그 자리에서 본문이 **요청이 아니라 응답의 이름**으로
 *     서 있던 것을 잡았다(아래 `useRecordCalibration` 머리말). 「때려 보고 모양을 확인한다」가
 *     왜 규율인지 보여 주는 두 번째 사례다.
 * 🔴 **쏴 보지 않은 것은 계약 기준으로만 서 있다** — 시약 lot · 폐기통 · 내보내기.
 *   그쪽을 붙일 때는 **먼저 한 번 때려 보고** 모양을 확인해라(`DELETE` 가 그래서 틀려 있었다).
 */

/**
 * 시료 등록.
 * 🔴 **필수는 `sample_id` 뿐이다.** 임상 3항목(적혈구 농도·채혈 날짜·암종)은 AI 학습 입력이라
 *    공정에는 안 쓰이지만 **배치 종료 후 소급 수집이 불가능**하다 — 등록 화면은 항상 노출하고
 *    미입력이면 경고한다(계약 산문 그대로).
 */
export const useRegisterSample = createCommand<
  {
    sampleId: string
    patientId?: string
    sampleType?: string
    rbcConcentration?: number
    bloodDrawDate?: string
    cancerType?: string
    cancerStage?: string
    /**
     * 자유 항목(`metadata` JSONB · DM-REL-06). 🔴 **기계가 시험 행을 가르는 자리다** —
     * 관리자 대시보드의 「테스트 배치」가 `{origin:'admin_test_batch', tag}` 를 여기 남긴다
     * (`app/resources/admin/pages/test_batch.js`). 접두(`TEST-…`)는 사람이 눈으로 가르는
     * 편의일 뿐이고, 나중에 회차를 가려 읽는 근거는 이 키다.
     */
    metadata?: Record<string, unknown>
    commandId?: string
  },
  Sample
>({
  send: (vars) =>
    orchestrator.request<Sample>({
      method: 'POST',
      path: '/samples',
      body: {
        command_id: vars.commandId ?? newCommandId(),
        sample_id: vars.sampleId,
        ...(vars.patientId === undefined ? {} : { patient_id: vars.patientId }),
        ...(vars.sampleType === undefined ? {} : { sample_type: vars.sampleType }),
        ...(vars.rbcConcentration === undefined
          ? {}
          : { rbc_concentration: vars.rbcConcentration }),
        ...(vars.bloodDrawDate === undefined ? {} : { blood_draw_date: vars.bloodDrawDate }),
        ...(vars.cancerType === undefined ? {} : { cancer_type: vars.cancerType }),
        ...(vars.cancerStage === undefined ? {} : { cancer_stage: vars.cancerStage }),
        ...(vars.metadata === undefined ? {} : { metadata: vars.metadata }),
      },
      retryOn401: true,
    }),
  invalidates: () => [keys.samples.all()],
})

/**
 * 시료 메타데이터 정정(부분 갱신).
 * 🔴 되돌릴 수 없는 정정이라 **사유가 필수**다. 보내지 않은 항목은 그대로 둔다.
 */
export const useCorrectSample = createCommand<
  { sampleId: string; reason: string; patch: Record<string, unknown>; commandId?: string },
  Sample
>({
  send: ({ sampleId, reason, patch, commandId }) =>
    orchestrator.request<Sample>({
      method: 'PUT',
      path: `/samples/${sampleId}`,
      body: { command_id: commandId ?? newCommandId(), reason, ...patch },
      retryOn401: true,
    }),
  invalidates: ({ sampleId }) => [keys.samples.detail(sampleId), keys.samples.all()],
})

/**
 * 레시피 커밋(신규). `payload` 는 `recipe.schema.json` 본문이다.
 *
 * 🔴 **`schemaVersion` 은 필수다.** 계약(`RecipeWriteEnvelope`)은 선택이라 적었지만 **실물이
 *    거부한다** — `app/views/recipes.py` 가 빈 값이면 422 `REQUEST_VALIDATION_FAIL`
 *    (`field: /schema_version`)을 낸다. 「판은 쓰는 쪽이 밝힌다 — 서버가 추측하지 않는다」.
 *    ⇒ 선택으로 두면 안 보낸 요청이 **전건 422** 다(사용자 관리 때 같은 함정이 있었다).
 * 🔴 **제어락이 필요 없다**(실물). 백엔드가 명시적으로 걷었다 —
 *    「여기에 `require_control_lock` 을 되살리면 계약에 없는 409 로 정상 커밋을 막는다」.
 *    ⚠ 반면 `PUT /recipes/{id}`(수정)는 락이 **필요**하다. 둘을 같이 묶지 마라.
 */
export const useCommitRecipe = createCommand<
  { name: string; payload: RecipePayload; schemaVersion: string; commandId?: string },
  Recipe
>({
  send: ({ name, payload, schemaVersion, commandId }) =>
    orchestrator.request<Recipe>({
      method: 'POST',
      path: '/recipes',
      body: {
        command_id: commandId ?? newCommandId(),
        name,
        payload,
        schema_version: schemaVersion,
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.recipes.all()],
})

/** 🔴 `X-Control-Token` 대상이다(실물 스펙). 클라이언트가 자동으로 붙인다 */
export const useUpdateRecipe = createCommand<
  { recipeId: string; name: string; payload: RecipePayload; commandId?: string },
  Recipe
>({
  send: ({ recipeId, name, payload, commandId }) =>
    orchestrator.request<Recipe>({
      method: 'PUT',
      path: `/recipes/${recipeId}`,
      body: { command_id: commandId ?? newCommandId(), name, payload },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: ({ recipeId }) => [keys.recipes.detail(recipeId), keys.recipes.all()],
})

/**
 * 보관(단방향). 🔴 사유 필수 — 되돌릴 수 없다.
 * ⚠ 응답은 **보관된 레시피 전문**이다(실물 `views/recipes.py` — 200 + Recipe DTO). `void` 가 아니다.
 */
export const useArchiveRecipe = createCommand<
  { recipeId: string; reason: string; commandId?: string },
  Recipe
>({
  send: ({ recipeId, reason, commandId }) =>
    orchestrator.request<Recipe>({
      method: 'POST',
      path: `/recipes/${recipeId}/archive`,
      body: { command_id: commandId ?? newCommandId(), reason },
      retryOn401: true,
    }),
  invalidates: ({ recipeId }) => [keys.recipes.detail(recipeId), keys.recipes.all()],
})

/** 검증. ⚠ **본문이 없다**(계약에 `requestBody` 미선언). 합격·불합격 모두 200 이다 */
export const useValidateRecipe = createCommand<{ recipeId: string }, ValidationResult>({
  send: ({ recipeId }) =>
    orchestrator.request<ValidationResult>({
      method: 'POST',
      path: `/recipes/${recipeId}/validate`,
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [],
})

/** 드라이런. ⚠ 본문 없음 */
export const useSimulateRecipe = createCommand<{ recipeId: string }, SimulationResult>({
  send: ({ recipeId }) =>
    orchestrator.request<SimulationResult>({
      method: 'POST',
      path: `/recipes/${recipeId}/simulate`,
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [],
})

export const useRegisterReagentLot = createCommand<
  { lot: string; name: string; expiry?: string; commandId?: string },
  void
>({
  send: ({ lot, name, expiry, commandId }) =>
    orchestrator.request<void>({
      method: 'POST',
      path: '/reagents/lots',
      body: {
        command_id: commandId ?? newCommandId(),
        lot,
        name,
        ...(expiry === undefined ? {} : { expiry }),
      },
      retryOn401: true,
    }),
  invalidates: () => [keys.reagentLots.all()],
})

/** 개봉 기록. 개봉 후 경과 시간의 기준점이 여기서 생긴다 */
export const useOpenReagentLot = createCommand<{ lot: string; commandId?: string }, void>({
  send: ({ lot, commandId }) =>
    orchestrator.request<void>({
      method: 'POST',
      path: `/reagents/lots/${lot}/open`,
      body: { command_id: commandId ?? newCommandId() },
      retryOn401: true,
    }),
  invalidates: () => [keys.reagentLots.all()],
})

export const useRecordWasteReplacement = createCommand<
  { containerId: string; note?: string; commandId?: string },
  void
>({
  send: ({ containerId, note, commandId }) =>
    orchestrator.request<void>({
      method: 'POST',
      path: `/waste-containers/${containerId}/replacement`,
      body: { command_id: commandId ?? newCommandId(), ...(note === undefined ? {} : { note }) },
      retryOn401: true,
    }),
  invalidates: () => [keys.wasteContainers.all()],
})

/**
 * 보정 결과 등재(201). `SVC-SCH-07` 시작 게이트의 판정 입력을 채우는 **유일한 계약 경로**다.
 *
 * 🔴 **요청과 응답의 이름이 다르다.** 종전 이 자리는 **응답 이름으로 본문을 만들고 있었다** —
 *    `target`·`result`·`valid_until` 은 `CalibrationRecord`(응답)의 칸이고, 요청
 *    `RecordCalibrationBody` 는 `module_id`·`passed`·`expires_at` 이다. 게다가 필수
 *    네 칸(`command_id`·`module_id`·`type`·`performed_at`) 중 셋이 선택으로 서 있어
 *    **부르는 순간 전건 422** 였다. 부르는 화면이 0건이라 아무도 못 봤다(2026-09-21 수정).
 *
 *    | 요청(`RecordCalibrationBody`) | 응답(`CalibrationRecord`) |
 *    | --- | --- |
 *    | `module_id` | `target` |
 *    | `passed`(bool) | `result`(`'pass'`\|`'fail'`) |
 *    | `expires_at` | `valid_until` |
 *
 * 🔴 **`performed_by` 를 보내지 않는다.** 계약 원문이 「서버가 토큰 주체로 채운다 —
 *    호출자가 적을 수 없다」(`IFC-COM-16`)이고 실물도 본문을 버린다
 *    (`views/maintenance.py` `performed_by=principal.user_id`). 보내면 조용히 사라지는 칸이다.
 * 🔴 **`expires_at` 에 기본 기한을 «만들지» 않는다.** 계약이 「경과 판정 기준값은 미결(OQ-121)
 *    — 계약은 자리만 정하고 값을 정하지 않는다」고 못 박았다. 「30일」 따위를 여기서 지어내면
 *    그것이 곧 하드코딩이고, 서버의 전역 판정(`(module_id, type)` 최신 1건)을 우리가 흔든다.
 *    비우면 **키째 안 보낸다** — `null` 도 보내지 않는다.
 * ⚠ **`passed` 는 3값이다**(통과·실패·미판정). 서버가 합격을 재판정하지 않고 받은 값을 그대로
 *   싣는다(`SVC-MODE-06` 정량 기준 미확정). 미판정은 `undefined` 로 두어 키째 뺀다.
 * ⚠ **`before_value`·`after_value` 를 여기서 받지 않는다**(2026-09-21 판단). 자유형 object 인데
 *   **응답에 되돌아오지 않아**(BR-36) 무엇이 저장됐는지 화면이 확인할 길이 없다. 확인할 수 없는
 *   입력칸은 「받는 척하고 버리는 칸」과 구분이 안 된다 — 응답에 실리면 그때 연다.
 * ⚠ **제어락이 필요 없다**(실물 `views/maintenance.py` — `require_control_lock` 0건 · 실측 201).
 *
 * ✅ 2026-09-21 실물 201 확인.
 */
export const useRecordCalibration = createCommand<
  {
    moduleId: string
    type: string
    /** ISO date-time. 🔴 **offset 이 있어야 한다** — naive 는 서버가 422 로 돌려보낸다 */
    performedAt: string
    /** 유효기한. 안 넘기면 본문에 키가 없다 */
    expiresAt?: string
    /** 판정. 안 넘기면 본문에 키가 없다(= 미판정) */
    passed?: boolean
    commandId?: string
  },
  CalibrationRecord
>({
  send: ({ moduleId, type, performedAt, expiresAt, passed, commandId }) =>
    orchestrator.request<CalibrationRecord>({
      method: 'POST',
      path: '/calibrations',
      body: {
        command_id: commandId ?? newCommandId(),
        module_id: moduleId,
        type,
        performed_at: performedAt,
        ...(expiresAt === undefined ? {} : { expires_at: expiresAt }),
        ...(passed === undefined ? {} : { passed }),
      },
      retryOn401: true,
    }),
  invalidates: () => [keys.calibrations.all()],
})

/** 내보내기 요청(202). 진행은 `GET /exports/{id}` 로 확인한다 */
export const useCreateExport = createCommand<
  {
    dataset: ExportRequest['dataset']
    format: ExportRequest['format']
    range?: { from?: string; to?: string }
    reason?: string
    commandId?: string
  },
  ExportJob
>({
  send: ({ dataset, format, range, reason, commandId }) =>
    orchestrator.request<ExportJob>({
      method: 'POST',
      path: '/exports',
      body: {
        command_id: commandId ?? newCommandId(),
        dataset,
        format,
        ...(range === undefined ? {} : { range }),
        ...(reason === undefined ? {} : { reason }),
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.exports.all()],
})

/**
 * 내보내기 **한 건을 끝까지** — 생성(202) → 완료 대기 → 파일 받기 → 저장.
 *
 * ## 🔴 실측 흐름 (2026-09-22 · 로컬 · dev-admin)
 *
 * ```
 * POST /exports                    202 {"export_id":"exp-…","status":"pending", …}
 * GET  /exports/{id}               200 {"status":"ready","download_url":"…","row_count":5, …}
 * GET  /exports/{id}/download      200 application/octet-stream
 *                                      content-disposition: attachment; filename="sample.csv"
 * ```
 *
 * 산출은 **응답 뒤 배경 작업**이라(`app/views/exports.py` `background.add_task`) 202 를 받은
 * 시점에는 아직 `pending` 이다. 실측으로는 26ms 만에 `ready` 가 됐지만 **그 빠르기에 기대지
 * 않는다** — 행 수가 늘면 길어진다.
 *
 * 🔴 **이 기다림은 「주기 폴링」이 아니다**(DP-01 예외가 아니라 **대상이 다르다**).
 *    DP-01 이 막는 것은 **상태를 긁는 주기 조회**이고, 여기 있는 것은 **사람이 방금 누른
 *    명령 한 건의 완료를 기다리는 것**이다. 근거 셋:
 *    ⓐ 구독 계약(`events.asyncapi.yaml`)에 **내보내기 채널이 없다** — 기다릴 다른 수단이 없다
 *    ⓑ 화면이 떠 있는 동안 도는 것이 아니라 **누른 뒤 끝날 때까지**만 돈다(상한 있음)
 *    ⓒ 대상이 **그 작업 한 건**이다. 목록을 긁지 않는다
 *
 * 🔴 **저장까지 이 안에서 한다.** 화면은 `Blob` 도 파일 이름도 모른다 — 호스트 차이(브라우저
 *    blob 내려받기 ↔ Tauri `plugin-dialog`+`plugin-fs`)는 `platform.files` 뒤에 있다.
 *    ⇒ 웹에서도 데스크톱 셸에서도 **화면 코드가 0줄도 안 바뀐다**.
 *
 * ## ⚠ 서버 구현이 계약보다 좁다 (2026-09-22 실측)
 *
 * 계약 enum 은 데이터셋 8종 · 형식 4종인데 **구현은 7종 · `csv`/`json` 뿐**이다
 * (`app/services/exports.py` `DATASETS` · `FORMATS`). **`batch_report` 데이터셋과
 * `pdf`·`xlsx` 형식은 422 `EXPORT_VALIDATION_FAIL`** 이다 — 부르는 쪽이 그 조합을
 * 단추로 세우지 마라(비활성 + 사유).
 */
export interface ExportFileVars {
  dataset: ExportRequest['dataset']
  format: ExportRequest['format']
  /** 데이터셋별 필터. 시료 보고서는 `{ batch_id }` 다 — 키가 그 데이터셋의 열이어야 422 를 안 본다 */
  filter?: Record<string, string>
  range?: { from?: string; to?: string }
  /**
   * 대량 반출 사유. 🔴 **프론트가 지어내지 않는다** — 사람이 적은 것만 싣는다.
   * ⚠ 계약은 `sample`·`audit_log`·`app_log` 에 필수라고 적었지만 **서버가 아직 안 받는다**
   *   (`create_export` 에 검사가 없고 `record_access` 가 `reason=None` 으로 남긴다 · 상신 계류).
   */
  reason?: string
  commandId?: string
}

export interface ExportedFile {
  /** 저장한 파일 이름 — 서버 `content-disposition` 이 준 것 그대로다 */
  fileName: string
  /** 서버가 센 행 수. 없으면 `undefined` — 0 으로 접지 않는다 */
  rowCount: number | undefined
}

/** 한 번 물어보는 간격과 전체 상한. 실측은 26ms 라 넉넉하다 — 상한을 명시로 남긴다 */
const EXPORT_POLL_INTERVAL_MS = 300
const EXPORT_POLL_TIMEOUT_MS = 30_000

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

/** `attachment; filename="sample.csv"` → `sample.csv`. 없으면 데이터셋·형식으로 짓는다 */
function fileNameOf(response: Response, job: ExportJob): string {
  const disposition = response.headers.get('content-disposition') ?? ''
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const fromServer = match?.[1]
  if (fromServer !== undefined && fromServer !== '') return decodeURIComponent(fromServer)
  return `${job.dataset ?? 'export'}.${job.format ?? 'bin'}`
}

export const useExportFile = createCommand<ExportFileVars, ExportedFile>({
  send: async ({ dataset, format, filter, range, reason, commandId }) => {
    const created = await orchestrator.request<ExportJob>({
      method: 'POST',
      path: '/exports',
      body: {
        command_id: commandId ?? newCommandId(),
        dataset,
        format,
        ...(range === undefined ? {} : { range }),
        ...(filter === undefined ? {} : { filter }),
        ...(reason === undefined ? {} : { reason }),
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    })

    const ready = await settled(created)

    const response = await orchestrator.requestRaw({
      method: 'GET',
      path: exportDownloadPath(ready.export_id),
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    })
    const fileName = fileNameOf(response, ready)
    await platform.files.save({
      fileName,
      // 서버는 늘 `application/octet-stream` 을 준다(내부 경로를 감추는 쪽을 택했다).
      // 받은 값을 그대로 쓴다 — 확장자는 파일 이름이 말한다.
      mimeType: response.headers.get('content-type') ?? 'application/octet-stream',
      data: await response.blob(),
    })

    return { fileName, rowCount: ready.row_count ?? undefined }
  },
  invalidates: () => [keys.exports.all()],
})

/**
 * `ready` 가 될 때까지. **`failed` 는 서버가 적어 준 까닭을 그대로 던진다** —
 * 「왜 실패했는지 조회 경로가 없어 재시도 말고 할 수 있는 일이 없던」 자리를 계약이
 * `ExportJob.error` 로 메웠다(생성 타입 주석). 그 값을 삼키지 않는다.
 */
async function settled(created: ExportJob): Promise<ExportJob> {
  const deadline = Date.now() + EXPORT_POLL_TIMEOUT_MS
  let job = created
  while (job.status === 'pending' || job.status === 'running') {
    if (Date.now() >= deadline) {
      throw new ApiError({ status: 0, message: t('error.exportTimeout') })
    }
    await wait(EXPORT_POLL_INTERVAL_MS)
    job = await orchestrator.request<ExportJob>({
      method: 'GET',
      path: `/exports/${job.export_id}`,
      retryOn401: true,
    })
  }
  if (job.status !== 'ready') {
    throw new ApiError({ status: 0, message: job.error ?? t('error.exportFailed') })
  }
  return job
}
